import { inject,Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';

import { SupportFacade } from '@personal-area/core';
import { I18nService } from '@personal-area/global/i18n';
import {
  MethodContextEnum,
  ModalResult,
  SocialMethodType,
  SocialNetworksEnum,
  UnavailableMethodData,
} from '@personal-area/global/social-authorization/models';
import {
  UnavailableGeneralModalComponent,
  UnavailableMethodModalComponent,
} from '@personal-area/global/unavailable-method-modal';

import { SocialAuthConfigService } from './social-auth-config.service';
import { SocialNetworksService } from './social-networks.service';

@Injectable({ providedIn: 'root' })
export class SocialAuthModalService {
  private readonly _router = inject(Router);
  private readonly _dialog = inject(MatDialog);
  private _i18nService = inject(I18nService);
  private _supportFacade = inject(SupportFacade);
  private _configService = inject(SocialAuthConfigService);
  private readonly _socialNetworksService = inject(SocialNetworksService);

  private readonly _methodsConfig: Record<
    SocialNetworksEnum,
    {
      name: string;
      icon: string;
      class: string;
    }
  > = {
    [SocialNetworksEnum.Facebook]: {
      name: 'Facebook',
      icon: 'i-btn-fb',
      class: 'facebook',
    },
    [SocialNetworksEnum.Google]: {
      name: 'Google',
      icon: 'i-btn-google',
      class: 'google',
    },
    [SocialNetworksEnum.Apple]: {
      name: 'Apple',
      icon: 'i-btn-apple',
      class: 'apple',
    },
  };

  private _getAvailableMethods(
    context: MethodContextEnum,
    excludeMethod: SocialNetworksEnum,
  ): {
    id: SocialNetworksEnum;
    name: string;
    icon: string;
    class: string;
  }[] {
    const disabledMethods = this._configService.getDisabledMethods(context);

    return Object.entries(this._methodsConfig)
      .filter(([methodId]) => {
        const method = methodId as SocialNetworksEnum;
        return method !== excludeMethod && !disabledMethods.includes(method);
      })
      .map(([methodId, config]) => ({
        id: methodId as SocialNetworksEnum,
        name: config.name,
        icon: config.icon,
        class: config.class,
      }));
  }

  private _handleSelectedMethod(
    method: SocialMethodType,
    context: MethodContextEnum,
  ): void {
    if (method === 'email') {
      this._redirectToEmailAuth(context);
    } else {
      this._socialNetworksService.login(method);
    }
  }

  private _handleAction(result: {
    action: 'resetPassword' | 'contactSupport';
    sourceMethod?: SocialNetworksEnum;
  }): void {
    switch (result.action) {
      case 'resetPassword':
        const queryParams = result.sourceMethod
          ? { fromModal: 'true', socialMethod: result.sourceMethod }
          : {};
        this._router.navigate(['/recovery'], { queryParams });
        break;
      case 'contactSupport':
        this._supportFacade.openChat();
        break;
    }
  }

  private _redirectToEmailAuth(context: MethodContextEnum): void {
    const route =
      context === MethodContextEnum.Registration
        ? '/registration/trader'
        : '/login';
    this._router.navigate([route]);
  }

  public showUnavailableGeneralModal(context: MethodContextEnum): void {
    this._dialog.open(UnavailableGeneralModalComponent, {
      data: { context },
      panelClass: 'unavailable-general-modal-container',
      direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
      disableClose: false,
    });
  }

  public showUnavailableModal({
    method,
    context,
  }: {
    method: SocialNetworksEnum;
    context: MethodContextEnum;
  }): Observable<ModalResult | undefined> {
    const methodConfig = this._methodsConfig[method];

    if (!methodConfig) {
      return of(undefined);
    }

    const dialogData: UnavailableMethodData = {
      method: {
        id: method,
        name: methodConfig.name,
        icon: methodConfig.icon,
      },
      context,
      availableMethods: this._getAvailableMethods(context, method),
    };

    return this._dialog
      .open(UnavailableMethodModalComponent, {
        data: dialogData,
        panelClass: 'unavailable-method-modal-container',
        direction: this._i18nService.isRtl ? 'rtl' : 'ltr',
        disableClose: false,
      })
      .afterClosed();
  }

  public handleModalResult(
    result: ModalResult,
    context: MethodContextEnum,
  ): void {
    if ('selectedMethod' in result) {
      this._handleSelectedMethod(result.selectedMethod, context);
    }

    if ('action' in result) {
      this._handleAction(result);
    }
  }
}
